import {
	IconButton,
	Panel,
	SearchSelect,
	SearchSelectOptionProps,
} from "@alm/flow-ds/components";
import api from "lib/api";
import _ from "lodash";
import {
	Dispatch,
	SetStateAction,
	useCallback,
	useEffect,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

interface Props {
	onAddMember?: (e: { label: string; value: string }) => void;
	onRemoveMember?: (username: string) => void;
	members: SearchSelectOptionProps[];
	setMembers: Dispatch<SetStateAction<SearchSelectOptionProps[]>>;
	type: "members" | "managers";
}

export default ({
	onAddMember,
	onRemoveMember,
	members,
	setMembers,
	type,
}: Props) => {
	const { t } = useTranslation();
	const history = useHistory();

	const [isPanelOpen, setIsPanelOpen] = useState(
		!onAddMember && !onRemoveMember
	);

	// State for member options
	const [optionsLoading, setOptionsLoading] = useState(false);
	const [memberOptions, setMemberOptions] = useState<
		SearchSelectOptionProps[] | undefined
	>(undefined);

	/**
	 * Adding a member to a role
	 *
	 * @param e {value: string; label: string}
	 */
	const handleAddMember = useCallback(
		e => {
			// onAddMember should only be available
			// when editing a role
			if (onAddMember) onAddMember(e);
			else {
				const currentMembers = [...members];
				// We only add the user to the members array
				// if it's not already added
				if (
					!members?.some(
						({ value }) =>
							_.lowerCase(value) === _.lowerCase(e.value)
					)
				)
					currentMembers.push(e);
				setMembers(currentMembers);
			}
		},
		[members, onAddMember, setMembers]
	);

	/**
	 * Removing a member from a role
	 *
	 * @param e event object
	 * @param index index in members array
	 */
	const handleRemoveMember = useCallback(
		(e, index) => {
			// onDeleteMember should only be available
			// when editing a role
			if (onRemoveMember) onRemoveMember(e.currentTarget.value);
			else {
				const currentMembers = [...members];
				currentMembers.splice(index, 1);
				setMembers(currentMembers);
			}
		},
		[members, onRemoveMember, setMembers]
	);

	// Get all users to use as options
	const getMemberOptions = useCallback(async () => {
		setOptionsLoading(true);
		try {
			const res = await api.roles.getAllUsers();
			const options = res.map((user: string) => {
				return { label: user, value: user };
			});
			setMemberOptions(options);
			setOptionsLoading(false);
		} catch (e) {
			console.error(e);
			// Redirect to roles page if the we can't load any users
			history.push({
				pathname: "/admin/roles",
			});
		}
	}, [history]);

	useEffect(() => {
		getMemberOptions();
	}, [getMemberOptions]);

	return (
		<Panel
			className="admin__role-form__members"
			isOpen={isPanelOpen}
			open={() => setIsPanelOpen(!isPanelOpen)}
			title={t(`main.admin.headings.${type}`)}
			id={`admin-${type}-panel`}>
			<SearchSelect
				className="admin__role-form__members-search"
				onChange={e => handleAddMember(e)}
				label={t("main.admin.text.memberSearch")}
				labelHidden={true}
				placeholder={t(`main.admin.text.${type}.searchForUser`)}
				options={memberOptions}
				value={null}
				loading={optionsLoading}
				disabled={optionsLoading}
			/>
			<div className="admin__role-form__members-title">
				{t(`main.admin.text.${type}.usersAssigned`)}
			</div>
			<div className="admin__role-form__members-list">
				{members.length
					? members?.map(
							(
								member: SearchSelectOptionProps,
								index: number
							) => {
								return (
									<div
										className="admin__role-form__members-item"
										key={`member-${index}`}>
										<div className="admin__role-form__members-item__name">
											{member.label}
										</div>
										<IconButton
											className="admin__role-form__members-item__delete"
											icon="times"
											label={t(
												"main.admin.labels.deleteRole"
											)}
											onClick={e =>
												handleRemoveMember(e, index)
											}
											value={member.value}
										/>
									</div>
								);
							}
					  )
					: t(`main.admin.text.${type}.noUsersAssigned`)}
			</div>
		</Panel>
	);
};
